/**
 * /app/{tenantSlug}/loans — Loans & Advances list (migration 064).
 *
 * Dual-audience landing:
 *   - employee: their own loans + outstanding rollup + "Request" CTA.
 *   - finance (company_admin / hr_manager / accountant): all loans, a
 *     status filter, and an approval-queue link with a pending badge.
 *
 * The backend is the boundary — it role-filters /loans server-side
 * (non-finance only ever gets its own rows); this page's role split is
 * UX-only.
 */

import { useMemo } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { HandCoins, Plus, Inbox } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { isAccountingAdmin } from "@/lib/nav";
import { useLoans, useLoanSummary } from "@/hooks/use-loans";
import type { Loan, LoanStatus } from "@/lib/api/loans";
import { formatPaise } from "@/lib/expense-format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/loans/")({
  component: LoansListRoute,
});

export function loanStatusVariant(
  s: LoanStatus,
): "default" | "secondary" | "destructive" | "outline" {
  switch (s) {
    case "disbursed":
      return "default";
    case "approved":
      return "secondary";
    case "rejected":
    case "written_off":
      return "destructive";
    default:
      return "outline";
  }
}

export function formatDuePeriod(year: number, month: number): string {
  const mm = String(month).padStart(2, "0");
  return `${year}-${mm}`;
}

function LoansListRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const finance = isAccountingAdmin(user?.role);

  const { data: loans, isLoading } = useLoans();
  const { data: summary } = useLoanSummary(undefined, !finance);

  const pendingCount = useMemo(
    () => (loans ?? []).filter((l) => l.status === "requested").length,
    [loans],
  );

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <HandCoins aria-hidden className="h-6 w-6 text-primary" />
            Loans &amp; Advances
          </h1>
          <p className="text-sm text-muted-foreground">
            Interest-free staff loans and salary advances, recovered via
            monthly payroll EMIs.
          </p>
        </div>
        <div className="flex gap-2">
          {finance && (
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/loans/approvals" params={{ tenantSlug }}>
                <Inbox aria-hidden className="h-4 w-4" />
                Approvals
                {pendingCount > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {pendingCount}
                  </Badge>
                )}
              </Link>
            </Button>
          )}
          <Button asChild className="gap-1.5" data-testid="loans-new-button">
            <Link to="/app/$tenantSlug/loans/new" params={{ tenantSlug }}>
              <Plus aria-hidden className="h-4 w-4" />
              Request
            </Link>
          </Button>
        </div>
      </div>

      {!finance && summary && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-6 pt-6">
            <div>
              <div className="text-xs uppercase text-muted-foreground">
                Outstanding
              </div>
              <div className="text-xl font-semibold">
                {formatPaise(summary.outstanding_paise)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase text-muted-foreground">
                Active loans
              </div>
              <div className="text-xl font-semibold">
                {summary.disbursed_count}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <LoanTable loans={loans} isLoading={isLoading} tenantSlug={tenantSlug} showEmployee={finance} />
    </div>
  );
}

export function LoanTable({
  loans,
  isLoading,
  tenantSlug,
  showEmployee,
}: {
  loans: Loan[] | undefined;
  isLoading: boolean;
  tenantSlug: string;
  showEmployee: boolean;
}) {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (!loans || loans.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No loans or advances yet.</p>
    );
  }
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            {showEmployee && <TableHead>Employee</TableHead>}
            <TableHead>Kind</TableHead>
            <TableHead className="text-right">Principal</TableHead>
            <TableHead className="text-right">Outstanding</TableHead>
            <TableHead className="text-center">Tenure</TableHead>
            <TableHead>Status</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loans.map((l) => (
            <TableRow key={l.id}>
              <TableCell className="font-mono">{l.id}</TableCell>
              {showEmployee && (
                <TableCell>{l.employee_name || `#${l.employee_id}`}</TableCell>
              )}
              <TableCell className="capitalize">{l.kind}</TableCell>
              <TableCell className="text-right">
                {formatPaise(l.principal_paise)}
              </TableCell>
              <TableCell className="text-right">
                {formatPaise(l.outstanding_paise)}
              </TableCell>
              <TableCell className="text-center">{l.tenure_months}m</TableCell>
              <TableCell>
                <Badge variant={loanStatusVariant(l.status)} className="capitalize">
                  {l.status.replace("_", " ")}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <Button asChild variant="ghost" size="sm">
                  <Link
                    to="/app/$tenantSlug/loans/$loanId"
                    params={{ tenantSlug, loanId: String(l.id) }}
                  >
                    View
                  </Link>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
